import { Cards, Flex, Text } from '@genially/public-web-components';
/* eslint-disable sort-imports */
import { useEffect, useState } from 'react';

import type { WrapperProps } from './WithLayout';
import { parseMedia } from 'core/infrastructure/parseMedia';
import styled from 'styled-components';
import { translate } from 'core/infrastructure/translator';
/* eslint-enable sort-imports */

/**
 * Props for the CommunityProfile component
 */
interface CommunityProfileProps {
  /** Location object containing URL search params */
  location: WrapperProps['location'];
  pageContext: WrapperProps['pageContext'];
}

/**
 * Represents a Genially item returned from the API
 */
interface GeniallyResult {
  /** Unique identifier of the Genially */
  id: string;
  /** Optional title of the Genially */
  title?: string;
  /** Optional name of the Genially */
  name?: string;
  /** URL of the Genially thumbnail image */
  thumbnail: string;
  /** Optional description of the Genially */
  description?: string;
  /** Creation timestamp */
  createdAt?: string;
  /** Last update timestamp */
  updatedAt?: string;
  useButtonText?: string;
  likesText?: string;
  reusesText?: string;
  modalTags?: string;
  modalTagsShowMore?: string;
  modalTagsShowLess?: string;
  modalViewProfile?: string;
}

/**
 * Profile data structure returned from the genially-searcher API
 */
interface ProfileData {
  /** Array of Genially items */
  results: GeniallyResult[];
  /** Total number of geniallys (alternative field) */
  total?: number;
  /** Count of geniallys in results (alternative field) */
  count?: number;
  /** Profile information */
  profile?: {
    /** Display name of the profile owner */
    name?: string;
    /** URL of the profile logo/avatar image */
    logo?: string;
  };
}

/**
 * Styled component for the user avatar box
 * Displays user initials in a rounded square container
 */
const AvatarBox = styled.div`
  width: 80px;
  height: 80px;
  background-color: rgb(199, 195, 211);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  color: white;
`;

/**
 * Styled component for the user avatar image
 * Displays the profile logo with proper sizing and rounded corners
 */
const AvatarImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
`;

/**
 * Styled component for the profile header section
 * Contains the avatar and profile information
 */
const ProfileHeader = styled.div`
  margin: 0 auto;
  background-color: rgb(249, 248, 252);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 24px;
  max-width: 1440px;
`;

const ProfileWrapper = styled.div`
  max-width: calc(1440px + 128px); /* 1440px content + 64px padding on each side */
  margin: 0 auto;
  padding-left: 64px;
  padding-right: 64px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding-left: 24px;
    padding-right: 24px;
  }
`;

/**
 * Styled component wrapper for the Cards section
 * Overrides internal Section and ComponentContainer to prevent overflow
 */
const CardsWrapper = styled.div`
  /* Override Section component width - it uses 100vw which causes overflow */
  & > div {
    width: 100% !important;
  }

  margin-bottom: 64px;

  /* Override internal ComponentContainer max-width and padding */
  article {
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

/**
 * CommunityProfile component displays a user's public profile page with their Geniallys
 *
 * This component fetches and displays a user's public Geniallys from the genially-searcher API.
 * It shows a profile header with an avatar (using initials), the user's name, and username,
 * followed by a grid of their public Genially creations.
 *
 * The component:
 * - Reads the profile ID from URL query parameter (?id=xxx)
 * - Fetches profile data from the genially-searcher API
 * - Displays profile information with an avatar generated from initials
 * - Shows all public Geniallys in a grid layout using the Cards component
 * - Cards open in a modal when clicked (socialProfile mode)
 *
 * @param {CommunityProfileProps} props - Component props
 * @param {WrapperProps['location']} props.location - Location object with search params
 *
 * @example
 * // URL: /community/profile/?id=user123
 * <CommunityProfile location={{ search: '?id=user123' }} />
 */
const CommunityProfile = ({ location, pageContext }: CommunityProfileProps) => {
  const forceSentenceCase = (text?: string): string => {
    if (!text || !text.trim()) return text ?? '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState<string>('');

  useEffect(() => {
    const fetchProfileData = async () => {
      // Extraer el communityProfileId del query parameter ?id=xxx
      const searchParams = new URLSearchParams(location.search);
      const communityProfileId = searchParams.get('id');

      if (communityProfileId) {
        setProfileId(communityProfileId);

        const endpoint = `https://genially-searcher.genially.com/api/profile/${communityProfileId}/geniallys?limit=48`;

        try {
          const response = await fetch(endpoint);
          const data = await response.json();
          setProfileData(data);
        } catch (error) {
          console.error('Error fetching profile data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [location.search]);

  /**
   * Converts Genially API results to Cards component format
   * Transforms the genially-searcher API response into the structure expected by the Cards component
   *
   * @param {GeniallyResult[]} results - Array of Genially items from the API
   * @returns Array of card objects compatible with the Cards component
   */
  const parseGeniallysToCards = (results: GeniallyResult[], langcode?: string) => {
    const translateFromContent = (key: string) => translate(langcode ? langcode : 'en', key);

    return results.map(genially => {
      const useButtonText = translateFromContent('community_usetemplate');
      const likesText = translateFromContent('community_likes');
      const reusesText = translateFromContent('community_reuses');
      const modalTags = translateFromContent('community_modal_tags');
      const modalTagsShowMore = translateFromContent('community_modal_tags_show_more');
      const modalTagsShowLess = translateFromContent('community_modal_tags_show_less');
      const modalViewProfile = translateFromContent('community_modal_viewprofile');
      const itemTitle = forceSentenceCase(genially.name || genially.title || '');

      // Crear estructura de teaserImage compatible con parseMedia
      const teaserImage = {
        entity: {
          type: 'MediaImage',
          mediaImage: {
            alt: itemTitle,
            title: itemTitle,
            width: 1200,
            height: 675,
            src: genially.thumbnail,
          },
        },
      };

      genially.useButtonText = useButtonText;
      genially.likesText = likesText;
      genially.reusesText = reusesText;
      genially.modalTags = modalTags;
      genially.modalTagsShowMore = modalTagsShowMore;
      genially.modalTagsShowLess = modalTagsShowLess;
      genially.modalViewProfile = modalViewProfile;

      return {
        media: parseMedia(teaserImage, true, 'en', itemTitle),
        title: {
          text: itemTitle,
        },
        url: '', // Las cards de community no tienen URL directa, se abren en modal
        fullTemplate: genially,
      };
    });
  };

  /**
   * Gets the total count of Genially designs for the profile
   * Tries multiple fields in order: count, total, or results array length
   *
   * @returns The total number of designs or 0 if no data available
   */
  const getDesignCount = () => {
    if (!profileData) return 0;
    return profileData.count ?? profileData.total ?? profileData.results?.length ?? 0;
  };

  /**
   * Generates initials from a user's name for the avatar
   * - If name has 2+ words, uses first letter of first two words
   * - If name has 1 word, uses first two characters
   *
   * @param {string} name - User's display name
   * @returns {string} Two uppercase letters for the avatar
   *
   * @example
   * getInitials("John Doe") // Returns "JD"
   * getInitials("Alice") // Returns "AL"
   */
  const getInitials = (name: string) => {
    const words = name
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.trim().substring(0, 2).toUpperCase();
  };

  const profileName = profileData?.profile?.name || profileId;
  const profileLogo = profileData?.profile?.logo;

  /**
   * Renders the appropriate avatar component based on available data
   * - If profile has a logo, displays the logo image
   * - Otherwise, displays initials in a colored box
   *
   * @returns {JSX.Element} Avatar component (either image or initials box)
   */
  const renderAvatar = () => {
    if (profileLogo) {
      return <AvatarImage src={profileLogo} alt={profileName} />;
    }
    return <AvatarBox>{getInitials(profileName)}</AvatarBox>;
  };

  const nullSpacing = {
    desktop: {
      top: 0,
      bottom: 80,
      left: 0,
      right: 0,
    },
    laptop: {
      top: 0,
      bottom: 80,
      left: 0,
      right: 0,
    },
    tablet: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    mobile: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  };

  return (
    <main>
      <ProfileWrapper>
        <ProfileHeader>
          <Flex alignItems="center" style={{ gap: '16px' }}>
            {renderAvatar()}
            <div>
              <Text variant="title1XL" style={{ marginBottom: '4px' }}>
                {profileName}
              </Text>
              <Text variant="paragraphS" style={{ color: 'rBlack' }}>
                @{profileId}
              </Text>
            </div>
          </Flex>
          <Text variant="paragraphS" style={{ marginTop: '16px' }}>
            {getDesignCount()} Design{getDesignCount() !== 1 ? 's' : ''}
          </Text>
        </ProfileHeader>
        <CardsWrapper>
          <Cards
            variant="grid"
            masonry
            templateCard
            socialProfile
            elementOpenOnModal
            customSpacing={nullSpacing}
            cards={
              profileData?.results
                ? parseGeniallysToCards(
                    profileData.results,
                    pageContext.node.data.content.langcode.value
                  )
                : []
            }
            isLoading={loading}
          />
        </CardsWrapper>
      </ProfileWrapper>
    </main>
  );
};

export default CommunityProfile;
